import type { Meeting, TodoItem } from "../../types";
import { useI18n } from "../../i18n";

export function Dashboard({
  meetings,
  todos,
  onOpenMeeting,
  onNewMeeting,
  onViewMeetings,
  onToggleAction,
  onToggleTodo,
}: {
  meetings: Meeting[];
  todos: TodoItem[];
  onOpenMeeting: (id: string) => void;
  onNewMeeting: () => void;
  onViewMeetings: () => void;
  onToggleAction: (meetingId: string, actionId: string) => void;
  onToggleTodo: (todo: TodoItem) => void;
}): React.JSX.Element {
  const { language, t } = useI18n();
  const openMeetingActions = meetings
    .flatMap((meeting) =>
      meeting.summary.actionItems.map((action) => ({ meeting, action })),
    )
    .filter(({ action }) => !action.done);
  const openTodos = todos.filter((todo) => !todo.completed);
  const openActionCount = openTodos.length + openMeetingActions.length;
  const timeSaved = meetings.length * 8;
  const formatter = new Intl.DateTimeFormat(language, {
    month: "short",
    day: "numeric",
  });
  return (
    <div className="page-stack">
      <section className="stats-grid">
        <article className="stat">
          <small>{t("dashboard.timeSaved")}</small>
          <strong>{timeSaved}</strong>
          <span>{t("dashboard.minutes")}</span>
        </article>
        <article className="stat">
          <small>{t("dashboard.meetings")}</small>
          <strong>{meetings.length}</strong>
          <span>{t("dashboard.meetingUnit")}</span>
        </article>
        <article className="stat">
          <small>{t("dashboard.actions")}</small>
          <strong>{openActionCount}</strong>
          <span>{t("dashboard.actionsTitle")}</span>
        </article>
        <article className="stat">
          <small>{t("dashboard.aiSpend")}</small>
          <strong>$0.00</strong>
          <span>{t("dashboard.demoMode")}</span>
        </article>
      </section>
      {meetings.length === 0 ? (
        <section className="panel empty-state">
          <div className="empty-icon">✦</div>
          <h2>{t("dashboard.emptyTitle")}</h2>
          <p>{t("dashboard.emptyBody")}</p>
          <button className="primary" type="button" onClick={onNewMeeting}>
            + {t("dashboard.newMeeting")}
          </button>
        </section>
      ) : (
        <section className="dashboard-grid">
          <article className="panel">
            <div className="panel-heading">
              <div>
                <small>{t("common.today")}</small>
                <h2>{t("dashboard.recent")}</h2>
              </div>
              <button
                className="text-button"
                type="button"
                onClick={onViewMeetings}
              >
                {t("dashboard.viewAll")}
              </button>
            </div>
            <div className="meeting-list">
              {meetings.slice(0, 4).map((meeting) => (
                <button
                  className="meeting-row"
                  type="button"
                  key={meeting.id}
                  onClick={() => onOpenMeeting(meeting.id)}
                >
                  <time>{formatter.format(new Date(meeting.updatedAt))}</time>
                  <div>
                    <strong>{meeting.title}</strong>
                    <span>{meeting.summary.executiveSummary}</span>
                    <div className="chips">
                      <b>
                        {meeting.summary.actionItems.length}{" "}
                        {t("meeting.actions").toLowerCase()}
                      </b>
                      <b>
                        {meeting.summary.decisions.length}{" "}
                        {t("meeting.decisions").toLowerCase()}
                      </b>
                    </div>
                  </div>
                  <span className="arrow">→</span>
                </button>
              ))}
            </div>
          </article>
          <article className="panel">
            <div className="panel-heading">
              <div>
                <small>{t("dashboard.actions")}</small>
                <h2>{t("dashboard.actionsTitle")}</h2>
              </div>
              <span className="count">{openActionCount}</span>
            </div>
            <div className="action-list">
              {openActionCount === 0 ? (
                <p className="muted">{t("dashboard.noActions")}</p>
              ) : (
                <>
                  {openTodos.slice(0, 4).map((todo) => (
                    <label className="action-check" key={todo.id}>
                      <input
                        type="checkbox"
                        checked={todo.completed}
                        onChange={() => onToggleTodo(todo)}
                        aria-label={t("dashboard.markDone")}
                      />
                      <span>
                        <strong>{todo.title}</strong>
                        <small>
                          {todo.owner || t("meeting.unassigned")} ·{" "}
                          {todo.dueDate}
                        </small>
                      </span>
                    </label>
                  ))}
                  {openMeetingActions
                    .slice(0, Math.max(0, 6 - openTodos.length))
                    .map(({ meeting, action }) => (
                      <label className="action-check" key={action.id}>
                        <input
                          type="checkbox"
                          checked={action.done}
                          onChange={() => onToggleAction(meeting.id, action.id)}
                          aria-label={t("dashboard.markDone")}
                        />
                        <span>
                          <strong>{action.task}</strong>
                          <small>
                            {action.owner || t("meeting.unassigned")} ·{" "}
                            {meeting.title}
                          </small>
                        </span>
                      </label>
                    ))}
                </>
              )}
            </div>
          </article>
        </section>
      )}
    </div>
  );
}
